// S11 · the Assessment card inside the interview-plan editor (CR-022: "HM + Recruiter assessment governance decided at
// intake: required?, type, stage/timing, owner, reviewers, criteria"; CR-069: the pinned *Interview recommendations*
// field, empty and written only by people). PlanEditor (S10) mounts it with the plan it read from `intake.plan_open`.
//
// One decision, written by `panel.decide_exercise` with the plan revision the editor saw: *Not Required* with its
// reason ("No assessment task when Not Required."), or *Required* with type (default option *case study*), stage (a round
// of the plan, named from its kind and sequence), owner, reviewers (one person pick per row), criteria, the case study
// and the due window. Each block saves on change or blur once the decision is complete (no save button); the card shows
// saving, *Recorded*, a refusal in his rule sentence with the entered values kept, or the error strip. The level's
// framework default is a *Suggested* value beside the choice, never a decision (D13, Q10). After the plan is finalized
// the reviewers stay editable through `exercise.assign_reviewers`, a removal with its reason.
//
// Seats (D3): the Hiring Manager, the Recruiter and the Admin edit (`can.edit` on the `full` projection; DC-228 folds his
// TA Admin into the Admin, who acts by the ordinary path); the HR Business Partner and the TA Manager read the values;
// the Coordinator's `scheduling` projection carries only the type, the
// stage and the due window, so nothing else is drawn. The server decides every write again.

import { useEffect, useId, useMemo, useState } from "react";
import {
  ASSESSMENT_STATUS, CORE_LABELS, COMMON_LABELS, G1_LABELS, LABEL_TEMPLATES, SURFACE_LABELS, ACTION_LABELS, Button, Card, Card2, EmptyState,
  Field, FieldState, Flex, Grid, IconButton, Input, KeyValue, PersonPick, Pill, Recorded, Segmented, Select, SectionTitle, Stack,
  SuggField, TextArea, type ErrorStripProps, type KeyValueItem, type PersonOption, type SegmentedOption, type SelectOption,
} from "@/mc";
import {
  exerciseAssignReviewers, panelDecideExercise, panelPeople, type ExerciseAssignReviewersInput, type IntakePlanOpenOutput,
  type PanelDecideExerciseInput,
} from "@/api/tools";
import { useRead, useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { ASSESSMENT_WORDS, FRAMEWORK_WORDS } from "./assessment/labels";
import {
  failureOf, peopleOf, planView, roundName, type AssessmentDefault, type PlanAssessmentLike, type PlanPersonLike, type PlanViewLike,
} from "./assessment/shapes";

/** At most six reviewers (`panel.decide_exercise` and `exercise.assign_reviewers`, `reviewer_ids` ≤ 6). */
const MAX_REVIEWERS = 6;
const DUE_MIN = 1;
const DUE_MAX = 30;
const DUE_DEFAULT = 5;

/** The decision as the card edits it. `required` null: the intake has not decided yet. */
export interface AssessmentDraft {
  required: boolean | null;
  assessment_type: string;
  stage_round_key: string;
  owner_id: string;
  reviewer_ids: readonly string[];
  criteria: readonly string[];
  brief_text: string;
  due_window_days: number;
  reason_when_no: string;
  recommendations_text: string;
}

export function draftOf(a: PlanAssessmentLike | null | undefined): AssessmentDraft {
  const decided = a ? (a.decided ?? a.required !== null) : false;
  return {
    required: decided && a ? a.required : null,
    assessment_type: a?.assessment_type ?? (decided && a?.required ? "" : ASSESSMENT_WORDS.caseStudy),
    stage_round_key: a?.stage_round_key ?? "",
    owner_id: a?.owner?.person_id ?? "",
    reviewer_ids: (a?.reviewers ?? []).map((person) => person.person_id),
    criteria: [...(a?.criteria ?? [])],
    brief_text: a?.brief_text ?? "",
    due_window_days: a?.due_window_days ?? DUE_DEFAULT,
    reason_when_no: a?.reason_when_no ?? "",
    recommendations_text: a?.recommendations_text ?? "",
  };
}

/** A decision the server can take: Not Required with a reason, or Required with every field his sentence names (the
 * owner defaults to the Recruiter on the server when left empty). An incomplete decision is kept on screen, unsaved. */
export function draftComplete(d: AssessmentDraft): boolean {
  if (d.required === null) return false;
  if (!d.required) return d.reason_when_no.trim().length > 0;
  return d.assessment_type.trim() !== ""
    && d.stage_round_key !== ""
    && d.reviewer_ids.length > 0
    && d.criteria.some((criterion) => criterion.trim() !== "")
    && d.brief_text.trim() !== "";
}

/** The `panel.decide_exercise` input (S08's fields beside V1's), stamped with the plan revision the editor saw. */
export interface DecideInput {
  requisition_id: string;
  required: boolean;
  brief_text?: string | null | undefined;
  criteria?: string[] | undefined;
  due_window_days?: number | undefined;
  attached_round_key?: string | null | undefined;
  reason_when_no?: string | null | undefined;
  assessment_type?: string | null | undefined;
  owner_id?: string | null | undefined;
  reviewer_ids?: string[] | null | undefined;
  recommendations_text?: string | null | undefined;
  expected_plan_revision: number;
}

export function decideInput(rid: string, d: AssessmentDraft, revision: number): DecideInput {
  const recommendations = d.recommendations_text.trim() || null;
  if (!d.required) {
    return { requisition_id: rid, required: false, reason_when_no: d.reason_when_no.trim(), recommendations_text: recommendations, expected_plan_revision: revision };
  }
  return {
    requisition_id: rid,
    required: true,
    brief_text: d.brief_text.trim(),
    criteria: d.criteria.map((criterion) => criterion.trim()).filter(Boolean),
    due_window_days: d.due_window_days,
    attached_round_key: d.stage_round_key || null,
    assessment_type: d.assessment_type.trim() || null,
    owner_id: d.owner_id || null,
    reviewer_ids: [...d.reviewer_ids],
    recommendations_text: recommendations,
    expected_plan_revision: revision,
  };
}

/** The framework default's word (his three; "recommended" quoted from G §5, a D0 change request). */
const DEFAULT_WORD: Readonly<Record<AssessmentDefault, string>> = {
  required: COMMON_LABELS.required,
  recommended: FRAMEWORK_WORDS.recommended,
  optional: COMMON_LABELS.optional,
};

export type CardSaveState = "idle" | "saving" | "saved";

export interface AssessmentCardViewProps {
  plan: PlanViewLike;
  /** The people a pick offers (`panel.people`, no email): HM, Recruiter and the plan's seated interviewers. */
  people?: readonly PersonOption[] | undefined;
  peopleLoading?: boolean | undefined;
  /** The finalized plan's reviewers may still change (`exercise.assign_reviewers`, the manifest grants it). */
  assignable?: boolean | undefined;
  state?: CardSaveState | undefined;
  /** The server refused the last write: his rule sentence; the entered values stay. */
  refused?: string | undefined;
  /** A system failure: the error strip with Retry. */
  failure?: ErrorStripProps | undefined;
  onDecide?: ((draft: AssessmentDraft) => void) | undefined;
  onAssign?: ((reviewerIds: string[], reason: string | null) => void) | undefined;
}

/** The people a pick can show: the offered list, plus the owner and reviewers already chosen (so a name never blanks). */
function withChosen(people: readonly PersonOption[], chosen: readonly (PlanPersonLike | null | undefined)[]): PersonOption[] {
  const out = [...people];
  for (const person of chosen) {
    if (person && !out.some((option) => option.value === person.person_id)) out.push({ value: person.person_id, name: person.name });
  }
  return out;
}

/** The card itself: no data access, so a seat's rendering is a function of the plan it was given. */
export function AssessmentCardView({
  plan, people = [], peopleLoading, assignable, state = "idle", refused, failure, onDecide, onAssign,
}: AssessmentCardViewProps) {
  const a = plan.assessment ?? null;
  const editable = plan.projection === "full" && plan.can?.edit === true;
  const [draft, setDraft] = useState<AssessmentDraft>(() => draftOf(a));
  const [dirty, setDirty] = useState(false);
  const [newCriterion, setNewCriterion] = useState("");
  // A newer plan (another editor's save, or this card's own) resets the card unless the person has unsaved entries.
  useEffect(() => {
    if (!dirty) setDraft(draftOf(a));
  }, [plan.plan_revision]);
  useEffect(() => {
    if (state === "saved") setDirty(false);
  }, [state]);

  const options = useMemo(() => withChosen(people, [a?.owner, ...(a?.reviewers ?? [])]), [people, a]);
  const stages: SelectOption[] = useMemo(
    () => (plan.rounds ?? [])
      .filter((round) => round.in_use || round.key === draft.stage_round_key)
      .flatMap((round) => {
        const name = roundName(round.kind, round.sequence);
        return name ? [{ value: round.key, label: name }] : [];
      }),
    [plan.rounds, draft.stage_round_key],
  );
  const types: SelectOption[] = useMemo(() => {
    const list: SelectOption[] = [{ value: ASSESSMENT_WORDS.caseStudy, label: ASSESSMENT_WORDS.caseStudy }];
    if (draft.assessment_type && draft.assessment_type !== ASSESSMENT_WORDS.caseStudy) list.push({ value: draft.assessment_type, label: draft.assessment_type });
    return list;
  }, [draft.assessment_type]);

  /** A change that saves now (a choice, a pick) when the decision is complete. */
  const commit = (next: AssessmentDraft) => {
    setDraft(next);
    setDirty(true);
    if (editable && draftComplete(next)) onDecide?.(next);
  };
  /** A text field's edit: kept until blur. */
  const edit = (next: AssessmentDraft) => {
    setDraft(next);
    setDirty(true);
  };
  const blur = () => {
    if (editable && dirty && draftComplete(draft)) onDecide?.(draft);
  };

  const decided = draft.required !== null;
  const pill = decided ? <Pill>{draft.required ? ASSESSMENT_STATUS.required : ASSESSMENT_STATUS.notRequired}</Pill> : undefined;
  const suggestion = !decided && a?.suggested_default ? (
    <Flex gap={8}>
      <FieldState kind="framework" />
      <Pill>{DEFAULT_WORD[a.suggested_default]}</Pill>
    </Flex>
  ) : null;

  const choice: SegmentedOption[] = [
    { value: "no", label: G1_LABELS.notRequired, hs: "plan.assessment.required.no" },
    { value: "yes", label: COMMON_LABELS.required, hs: "plan.assessment.required" },
  ];
  // Undecided: no choice is marked (the framework default is only Suggested).
  const chosen = draft.required === null ? "" : draft.required ? "yes" : "no";

  const body = editable ? (
    <Stack gap={14}>
      <Flex gap={10} align="center" wrap>
        <Segmented
          options={choice}
          value={chosen}
          label={ASSESSMENT_WORDS.assessment}
          onChange={(value) => commit({ ...draft, required: value === "yes" })}
        />
        {suggestion}
      </Flex>
      {draft.required === false ? (
        <>
          <EmptyState inline>{CORE_LABELS.noAssessmentTask}</EmptyState>
          <Field label={G1_LABELS.reason} required>
            <TextArea
              value={draft.reason_when_no}
              maxLength={500}
              rows={2}
              
              onChange={(event) => edit({ ...draft, reason_when_no: event.target.value })}
              onBlur={blur}
            />
          </Field>
        </>
      ) : null}
      {draft.required === true ? (
        <Governance
          draft={draft}
          options={options}
          peopleLoading={peopleLoading}
          stages={stages}
          types={types}
          newCriterion={newCriterion}
          setNewCriterion={setNewCriterion}
          commit={commit}
          edit={edit}
          blur={blur}
        />
      ) : null}
      {state === "saved" && !dirty ? <Recorded live /> : null}
    </Stack>
  ) : (
    <Stack gap={14}>
      {suggestion}
      <AssessmentValues plan={plan} reviewers={!(assignable && a?.required)} />
      {assignable && a?.required ? (
        <ReviewerChange current={(a.reviewers ?? []).map((person) => person.person_id)} options={options} peopleLoading={peopleLoading} onAssign={onAssign} />
      ) : null}
      {state === "saved" ? <Recorded live /> : null}
    </Stack>
  );

  return (
    <Card
      title={ASSESSMENT_WORDS.assessment}
      pills={pill}
      refused={refused ? { reason: refused } : undefined}
      error={failure}
      id="plan-assessment"
    >
      {body}
    </Card>
  );
}

interface GovernanceProps {
  draft: AssessmentDraft;
  options: readonly PersonOption[];
  peopleLoading?: boolean | undefined;
  stages: readonly SelectOption[];
  types: readonly SelectOption[];
  newCriterion: string;
  setNewCriterion: (value: string) => void;
  commit: (next: AssessmentDraft) => void;
  edit: (next: AssessmentDraft) => void;
  blur: () => void;
}

/** Required: type, stage, owner, due window; the reviewers; the criteria; the case study; the pinned field. */
function Governance({ draft, options, peopleLoading, stages, types, newCriterion, setNewCriterion, commit, edit, blur }: GovernanceProps) {
  const reviewersTitle = useId();
  const criteriaTitle = useId();
  const reviewers = draft.reviewer_ids;
  const criteria = draft.criteria;
  return (
    <Card2>
      <Stack gap={14}>
        <Grid cols={2}>
          <Field label={ASSESSMENT_WORDS.type} required>
            <Select options={types} value={draft.assessment_type} required  onChange={(value) => commit({ ...draft, assessment_type: value })} />
          </Field>
          <Field label={ASSESSMENT_WORDS.stage} required>
            <Select options={stages} value={draft.stage_round_key} required  onChange={(value) => commit({ ...draft, stage_round_key: value })} />
          </Field>
          <Field label={SURFACE_LABELS.owner}>
            <PersonPick options={options} value={draft.owner_id} loading={peopleLoading}  onChange={(value) => commit({ ...draft, owner_id: value })} />
          </Field>
          <Field label={ASSESSMENT_WORDS.due} required hint={LABEL_TEMPLATES.dueIn({ n: draft.due_window_days })}>
            <Input
              type="number"
              min={DUE_MIN}
              max={DUE_MAX}
              value={String(draft.due_window_days)}
              
              onChange={(event) => {
                const n = Math.round(Number(event.target.value));
                if (Number.isFinite(n)) edit({ ...draft, due_window_days: Math.min(DUE_MAX, Math.max(DUE_MIN, n)) });
              }}
              onBlur={blur}
            />
          </Field>
        </Grid>

        <div role="group" aria-labelledby={reviewersTitle}>
          <SectionTitle id={reviewersTitle} count={reviewers.length}>{ASSESSMENT_WORDS.reviewers}</SectionTitle>
          <Stack gap={8}>
            {reviewers.map((id, index) => (
              <Flex key={`${id}-${index}`} gap={8} align="center">
                <PersonPick
                  options={options.filter((option) => option.value === id || !reviewers.includes(option.value))}
                  value={id}
                  label={ASSESSMENT_WORDS.reviewers}
                  loading={peopleLoading}
                  hs={`plan.assessment.reviewer.${index + 1}`}
                  onChange={(value) => commit({ ...draft, reviewer_ids: reviewers.map((old, at) => (at === index ? value : old)) })}
                />
                <IconButton
                  icon="x"
                  sm
                  label={ASSESSMENT_WORDS.remove}
                  onClick={() => commit({ ...draft, reviewer_ids: reviewers.filter((_, at) => at !== index) })}
                />
              </Flex>
            ))}
            {reviewers.length < MAX_REVIEWERS ? (
              <PersonPick
                options={options.filter((option) => !reviewers.includes(option.value))}
                value=""
                label={ASSESSMENT_WORDS.reviewers}
                loading={peopleLoading}
                required={reviewers.length === 0}
                
                onChange={(value) => commit({ ...draft, reviewer_ids: [...reviewers, value] })}
              />
            ) : null}
          </Stack>
        </div>

        <div role="group" aria-labelledby={criteriaTitle}>
          <SectionTitle id={criteriaTitle} count={criteria.length}>{ASSESSMENT_WORDS.criteria}</SectionTitle>
          <Stack gap={8}>
            {criteria.map((criterion, index) => (
              <Flex key={index} gap={8} align="center">
                <Input
                  value={criterion}
                  maxLength={200}
                  aria-label={ASSESSMENT_WORDS.criteria}
                  hs={`plan.assessment.criterion.${index + 1}`}
                  onChange={(event) => edit({ ...draft, criteria: criteria.map((old, at) => (at === index ? event.target.value : old)) })}
                  onBlur={blur}
                />
                <IconButton
                  icon="x"
                  sm
                  label={ASSESSMENT_WORDS.remove}
                  onClick={() => commit({ ...draft, criteria: criteria.filter((_, at) => at !== index) })}
                />
              </Flex>
            ))}
            <Input
              value={newCriterion}
              maxLength={200}
              aria-label={ASSESSMENT_WORDS.criteria}
              required={criteria.length === 0}
              
              onChange={(event) => setNewCriterion(event.target.value)}
              onBlur={() => {
                const text = newCriterion.trim();
                if (!text) return;
                setNewCriterion("");
                commit({ ...draft, criteria: [...criteria, text] });
              }}
            />
          </Stack>
        </div>

        <Field label={ASSESSMENT_WORDS.brief} required>
          <TextArea
            value={draft.brief_text}
            maxLength={4000}
            rows={4}
            
            onChange={(event) => edit({ ...draft, brief_text: event.target.value })}
            onBlur={blur}
          />
        </Field>

        <div onBlur={blur}>
          <SuggField
            feature="interviewRecommendations"
            value={draft.recommendations_text}
            
            onChange={(value) => edit({ ...draft, recommendations_text: value })}
          />
        </div>
      </Stack>
    </Card2>
  );
}

/** The decision as values (a reading seat, or the plan after Finalize). Only what the projection carries is drawn: the
 * Coordinator's scheduling projection has no reviewers, criteria, case study or recommendations. */
function AssessmentValues({ plan, reviewers = true }: { plan: PlanViewLike; reviewers?: boolean | undefined }) {
  const a = plan.assessment ?? null;
  if (!a || a.required === null || (a.decided === false)) return null;
  if (!a.required) {
    const items: KeyValueItem[] = a.reason_when_no ? [[G1_LABELS.reason, a.reason_when_no]] : [];
    return (
      <>
        <EmptyState inline>{CORE_LABELS.noAssessmentTask}</EmptyState>
        {items.length ? <KeyValue items={items} cols={1} /> : null}
      </>
    );
  }
  const stage = a.stage_round_key ? (plan.rounds ?? []).find((round) => round.key === a.stage_round_key) : undefined;
  const stageName = stage ? roundName(stage.kind, stage.sequence) : null;
  const items: KeyValueItem[] = [];
  if (a.assessment_type) items.push([ASSESSMENT_WORDS.type, a.assessment_type]);
  if (stageName) items.push([ASSESSMENT_WORDS.stage, stageName]);
  if (a.owner) items.push([SURFACE_LABELS.owner, a.owner.name]);
  if (a.due_window_days) items.push([ASSESSMENT_WORDS.due, LABEL_TEMPLATES.dueIn({ n: a.due_window_days })]);
  if (reviewers && a.reviewers && a.reviewers.length) items.push([ASSESSMENT_WORDS.reviewers, a.reviewers.map((person) => person.name).join(" · "), undefined, 2]);
  if (a.criteria && a.criteria.length) items.push([ASSESSMENT_WORDS.criteria, a.criteria.join(" · "), undefined, 3]);
  if (a.brief_text) items.push([ASSESSMENT_WORDS.brief, a.brief_text, undefined, 3]);
  if (a.recommendations_text) items.push([G1_LABELS.interviewRecommendations, a.recommendations_text, undefined, 3]);
  return items.length ? <KeyValue items={items} cols={3} /> : null;
}

/** After Finalize: the reviewers change through `exercise.assign_reviewers`; a removal records its reason (D13). */
function ReviewerChange({ current, options, peopleLoading, onAssign }: {
  current: readonly string[];
  options: readonly PersonOption[];
  peopleLoading?: boolean | undefined;
  onAssign?: ((reviewerIds: string[], reason: string | null) => void) | undefined;
}) {
  const title = useId();
  const [ids, setIds] = useState<readonly string[]>(current);
  const [reason, setReason] = useState("");
  const saved = current.join("|");
  // A newer plan (after a save) resets the list to the stored reviewers.
  useEffect(() => { setIds(current); setReason(""); }, [saved]);
  const removed = current.filter((id) => !ids.includes(id));
  const add = (value: string) => {
    const next = [...ids, value];
    setIds(next);
    if (removed.length === 0) onAssign?.(next, null);
  };
  return (
    <div role="group" aria-labelledby={title}>
      <SectionTitle id={title} count={ids.length}>{ASSESSMENT_WORDS.reviewers}</SectionTitle>
      <Stack gap={8}>
        {ids.map((id, index) => (
          <Flex key={`${id}-${index}`} gap={8} align="center">
            <PersonPick options={options} value={id} label={ASSESSMENT_WORDS.reviewers} mode="view" />
            <IconButton icon="x" sm label={ASSESSMENT_WORDS.remove} hs={`plan.assessment.reviewer.${index + 1}`} onClick={() => setIds(ids.filter((_, at) => at !== index))} />
          </Flex>
        ))}
        {ids.length < MAX_REVIEWERS ? (
          <PersonPick
            options={options.filter((option) => !ids.includes(option.value))}
            value=""
            label={ASSESSMENT_WORDS.reviewers}
            loading={peopleLoading}
            
            onChange={add}
          />
        ) : null}
        {removed.length > 0 ? (
          <>
            <Field label={G1_LABELS.reason} required>
              <TextArea value={reason} maxLength={500} rows={2} onChange={(event) => setReason(event.target.value)} />
            </Field>
            <Flex gap={8}>
              <Button
                tone="primary"
                onClick={() => {
                  if (reason.trim().length >= 3 && ids.length > 0) onAssign?.([...ids], reason.trim());
                }}
              >
                {ASSESSMENT_WORDS.record}
              </Button>
              <Button tone="ghost" onClick={() => { setIds(current); setReason(""); }}>{ACTION_LABELS.cancel}</Button>
            </Flex>
          </>
        ) : null}
      </Stack>
    </div>
  );
}

/**
 * The card with its data path: the plan PlanEditor read, `panel.people` for the picks, `panel.decide_exercise` for the
 * decision (with the plan revision) and `exercise.assign_reviewers` after Finalize. `onChanged` refetches the plan.
 */
export function AssessmentCard({ rid, plan, onChanged }: { rid: string; plan: IntakePlanOpenOutput; onChanged: () => void }) {
  const view = planView(plan);
  const { can } = useSession();
  const editable = view.projection === "full" && view.can?.edit === true;
  const assignable = !editable && view.projection === "full" && !!view.finalized_by && view.assessment?.required === true
    && can("exercise.assign_reviewers");
  const people = useRead(["plan-people", rid], () => panelPeople({ requisition_id: rid }), { enabled: editable || assignable });
  const inval = [["intake", rid], ["plan", rid], ["exercise"]] as const;
  const decide = useWrite((input: DecideInput, key: string) => panelDecideExercise(input as PanelDecideExerciseInput, key), inval);
  const assign = useWrite(
    (input: { requisition_id: string; reviewer_ids: string[]; reason: string | null }, key: string) =>
      exerciseAssignReviewers(input as ExerciseAssignReviewersInput, key),
    inval,
  );
  const options: PersonOption[] = useMemo(
    () => peopleOf(people.data).map((person) => ({ value: person.id, name: person.name, seat: (person.role_words ?? []).join(" · ") || undefined })),
    [people.data],
  );
  const error = decide.error ?? assign.error;
  const { refused, failure } = failureOf(error, () => { decide.reset(); assign.reset(); onChanged(); });
  const state: CardSaveState = decide.isPending || assign.isPending ? "saving" : decide.isSuccess || assign.isSuccess ? "saved" : "idle";
  return (
    <AssessmentCardView
      plan={view}
      people={options}
      peopleLoading={people.isLoading}
      assignable={assignable}
      state={state}
      refused={refused}
      failure={failure}
      onDecide={(draft) => decide.mutate(decideInput(rid, draft, view.plan_revision), {
        onSuccess: () => onChanged(),
        // A stale revision: the newer plan is read, the person's entries stay (DC-24 refused).
        onError: (e) => { if (e.status === 409) onChanged(); },
      })}
      onAssign={(reviewerIds, reason) => assign.mutate({ requisition_id: rid, reviewer_ids: reviewerIds, reason }, { onSuccess: () => onChanged() })}
    />
  );
}
